<script setup>
import { computed, ref } from "vue";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import ExpertListCover from "@/Components/Profile/ExpertListCover.vue";
import { useMastheadImageUrls } from "@/composables/useMastheadImageUrls.js";

const props = defineProps({
    list: {
        type: Object,
        required: true,
    },
    // Accent color behind the cover image (mockup alternates gold / lime).
    accent: {
        type: String,
        default: "#ffb545",
    },
});

const avatarFailed = ref(false);

// Cover-less lists arrive as Cloudinary's blank "missing-image" fallback
// rather than null (GH-1062).
const coverImage = computed(() =>
    props.list.image && !props.list.image.includes("missing-image")
        ? props.list.image
        : null,
);

// The list's header photo on the owner's focal point, as on the grid's
// ExpertCard (GH-1485); without one, or on a 404, the cover falls back to
// the same editorial art with the item photo inset.
const headerFailed = ref(false);
const headerImage = computed(() => {
    const { masthead_photo, masthead_focus_x, masthead_focus_y } = props.list;

    if (!masthead_photo || headerFailed.value) {
        return null;
    }

    const focus =
        Number.isFinite(masthead_focus_x) && Number.isFinite(masthead_focus_y)
            ? { x: masthead_focus_x, y: masthead_focus_y }
            : null;

    return useMastheadImageUrls(masthead_photo, focus);
});
</script>

<template>
    <article
        class="relative flex flex-col overflow-hidden rounded-brand bg-brand-navy md:h-[325px] md:flex-row"
    >
        <!-- Accent strip: top edge on mobile, left edge on desktop -->
        <div
            aria-hidden="true"
            class="absolute inset-x-0 top-0 h-[86px] md:inset-y-0 md:left-0 md:h-auto md:w-[162px]"
            :style="{ backgroundColor: accent }"
        ></div>

        <!-- Cover image with creator chip -->
        <div class="relative mx-2.5 mt-2.5 shrink-0 md:m-[17px]">
            <div
                class="relative h-[150px] overflow-hidden rounded-[4px] bg-brand-navy/40 md:h-[288px] md:w-[291px]"
            >
                <picture v-if="headerImage" class="block size-full">
                    <source
                        media="(min-width: 768px)"
                        :srcset="headerImage.featureSquare2xSrc"
                    />
                    <img
                        :src="headerImage.featureStrip2xSrc"
                        :alt="list.title"
                        class="size-full object-cover"
                        :style="{ objectPosition: headerImage.position }"
                        data-test="featured-list-header-photo"
                        @error="headerFailed = true"
                    />
                </picture>
                <ExpertListCover v-else :image="coverImage" layout="card" />
                <div
                    aria-hidden="true"
                    class="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent"
                ></div>
            </div>

            <div class="absolute bottom-3 left-3 flex items-center gap-2.5">
                <span
                    class="size-[25px] shrink-0 overflow-hidden rounded-[2px] bg-white/30"
                >
                    <img
                        v-if="list.avatar && !avatarFailed"
                        :src="list.avatar"
                        :alt="list.creator"
                        class="size-full rounded-[2px] object-cover"
                        @error="avatarFailed = true"
                    />
                </span>
                <span class="font-brand text-[14px] font-medium text-white">
                    {{ list.creator }}
                </span>
            </div>
        </div>

        <!--
            Copy + CTA; the feature cards carry no description (GH-1427).
            The column may shrink (min-w-0) and the title breaks
            inside it, so a long word wraps rather than clipping at the card
            edge; the side-by-side pair (1440px up) runs a little tighter so
            both cards fit.
        -->
        <div
            class="relative flex min-w-0 flex-1 flex-col px-[27px] pb-7 pt-5 md:justify-center md:py-8 md:pl-9 md:pr-10 min-[1440px]:pr-6"
        >
            <p
                class="font-brand-display text-[16px] font-semibold italic uppercase leading-none text-[#b1a7a5]"
            >
                Newly Added
            </p>
            <h3
                class="mt-2 max-w-[290px] font-brand-display text-[28px] font-black italic uppercase leading-[0.95] tracking-[-0.02em] text-white [overflow-wrap:anywhere] md:text-[35px] min-[1440px]:max-2xl:text-[30px]"
            >
                {{ list.title }}
            </h3>
            <BrandButton
                :href="list.url"
                variant="white"
                class="mt-6 h-11 w-full max-w-full px-6 text-[15px] md:w-[182px]"
            >
                View Expert List
            </BrandButton>
        </div>
    </article>
</template>
